import { API_ENDPOINTS } from '../../config/apiEndpoints';
import { User, ChangePasswordPayload } from '../../types';
import apiClient from '../../utils/apiClient';
import ConfigurationService from '../ConfigurationService';

export interface LoginCredentials {
  username: string;
  password?: string;
  captchaToken?: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user?: User;
  accessToken?: string; // Changed from 'token' to match backend response
  // Two-factor authentication
  twoFactorRequired?: boolean;
  partialToken?: string;
  // Force password change on first login
  requiresPasswordChange?: boolean;
  firstLoginToken?: string;
}

export interface RefreshResponse {
  accessToken: string;
}

export interface ChangePasswordFirstLoginRequest {
  firstLoginToken: string;
  tempPassword: string;
  newPassword: string;
}

export interface ChangePasswordFirstLoginResponse {
  user: User;
  accessToken: string;
}

export const authApi = {
  /**
   * Authenticate a user with credentials and captcha.
   */
  login: async (credentials: LoginCredentials, options: Partial<import('../../utils/apiClient').LegacyApiClientOptions> = {}): Promise<LoginResponse> => {
    return apiClient<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, {
      method: 'POST',
      data: credentials,
      timeout: ConfigurationService.getApiTimeoutMs(),
      ...options,
    });
  },

  /**
   * Refresh the access token using the HttpOnly cookie.
   * Leverages the synchronized refresh logic in the NetworkService.
   */
  refresh: async (): Promise<RefreshResponse> => {
    const { refreshAuthToken } = await import('../../utils/apiClient');
    const token = await refreshAuthToken();
    if (!token) {
      throw new Error('Refresh failed');
    }
    return { accessToken: token };
  },

  /**
   * Fetch the current authenticated user's profile.
   */
  me: async (): Promise<User> => {
    return apiClient<User>(API_ENDPOINTS.AUTH.ME, { timeout: ConfigurationService.getApiTimeoutMs() });
  },

  /**
   * Logout the user.
   */
  logout: async (): Promise<void> => {
    return apiClient(API_ENDPOINTS.AUTH.LOGOUT, {
      method: 'POST',
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },

  /**
   * Verify current user's password for re-authentication.
   * Used before critical security operations to prevent session hijacking attacks.
   * 
   * @security This endpoint is rate-limited and all attempts are logged.
   * @param password - The user's current password
   * @param scope - Optional operation scope to authorize via elevation token
   * @returns Promise resolving to success confirmation and optional elevationToken
   */
  verifyPassword: async (password: string, scope?: string): Promise<{ success: boolean; message: string; elevationToken?: string }> => {
    return apiClient<{ success: boolean; message: string; elevationToken?: string }>(API_ENDPOINTS.AUTH.VERIFY_PASSWORD, {
      method: 'POST',
      data: { password, scope },
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },

  /**
   * Change password on first login.
   * Used when user has PENDING_FIRST_LOGIN status and must set their own password.
   *
   * @security This uses a short-lived firstLoginToken from the login response.
   * @security Rate-limited to prevent brute force attacks.
   * @param data - Object containing firstLoginToken, tempPassword, and newPassword
   * @returns Promise resolving to user and access token
   */
  changePasswordFirstLogin: async (data: ChangePasswordFirstLoginRequest): Promise<ChangePasswordFirstLoginResponse> => {
    return apiClient<ChangePasswordFirstLoginResponse>(API_ENDPOINTS.AUTH.CHANGE_PASSWORD_FIRST_LOGIN, {
      method: 'POST',
      data,
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },
  /**
   * Change current user's password.
   */
  changePassword: async (data: ChangePasswordPayload): Promise<void> => {
    return apiClient(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
      method: 'POST',
      data,
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },

  /**
   * Generate 2FA secret for enrollment.
   */
  generate2faSecret: async (): Promise<{ qrCodeDataUrl: string; secret: string }> => {
    return apiClient<{ qrCodeDataUrl: string; secret: string }>(API_ENDPOINTS.AUTH.TWO_FACTOR.GENERATE, {
      method: 'POST',
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },

  /**
   * Verify and enable 2FA.
   */
  verify2fa: async (token: string): Promise<void> => {
    return apiClient(API_ENDPOINTS.AUTH.TWO_FACTOR.VERIFY, {
      method: 'POST',
      data: { token },
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },

  /**
   * Disable 2FA for current user.
   */
  disable2fa: async (): Promise<void> => {
    return apiClient(API_ENDPOINTS.AUTH.TWO_FACTOR.DISABLE, {
      method: 'POST',
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },

  /**
   * Request a password reset link.
   */
  forgotPassword: async (data: { email: string; captchaToken: string }): Promise<{ message: string }> => {
    return apiClient<{ message: string }>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      method: 'POST',
      data,
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },

  /**
   * Reset password using a token.
   */
  resetPassword: async (data: { token?: string; password?: string }): Promise<{ message: string }> => {
    return apiClient<{ message: string }>(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      method: 'POST',
      data,
      timeout: ConfigurationService.getApiTimeoutMs(),
    });
  },
};
